import { ChangeEvent, DragEvent, FC, useState } from 'react';
import { Modal } from '../../../components';
import { UploadImageSVG } from '../../../components/svgs';
import { uploadAnimalDocument } from '../../../api/animalsApi';
import { showToast } from '../../../services/toast.service';
import { toastMessages } from '../../../constants/errorMessages';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS } from '../../../constants/common';
import './addDocumentModal.scss';

const MIN_DOCUMENT_SIZE_BYTES = 1;
const MAX_DOCUMENT_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_DOCUMENT_EXTENSION_LIST = ['pdf', 'png', 'jpeg', 'jpg'];
const ACCEPTED_DOCUMENT_EXTENSIONS = new Set(ACCEPTED_DOCUMENT_EXTENSION_LIST);
const ACCEPTED_DOCUMENT_FILE_TYPES = ACCEPTED_DOCUMENT_EXTENSION_LIST.map(extension => `.${extension}`).join(',');
const ACCEPTED_DOCUMENT_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

type AddDocumentModalProps = {
  animalId?: string;
  isOpen: boolean;
  onClose: () => void;
  onUploaded: () => void;
};

const isValidDocumentFile = (file: File): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension || fileExtension === 'jfif') {
    return false;
  }

  const hasValidExtension = ACCEPTED_DOCUMENT_EXTENSIONS.has(fileExtension);
  const hasValidMimeType = !file.type || ACCEPTED_DOCUMENT_MIME_TYPES.has(file.type);
  return hasValidExtension && hasValidMimeType;
};

const AddDocumentModal: FC<AddDocumentModalProps> = ({ animalId, isOpen, onClose, onUploaded }) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [description, setDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const resetHandler = () => {
    setSelectedFile(undefined);
    setDescription('');
    setIsSubmitting(false);
    onClose();
  };

  const applySelectedFile = (file: File) => {
    if (!isValidDocumentFile(file)) {
      showToast.error(toastMessages.INVALID_DOCUMENT_FILE_TYPE);
      return;
    }

    if (file.size < MIN_DOCUMENT_SIZE_BYTES) {
      showToast.error(toastMessages.DOCUMENT_EMPTY);
      return;
    }

    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      showToast.error(toastMessages.DOCUMENT_LARGER_THAN_5MB);
      return;
    }

    setSelectedFile(file);
  };

  const fileChangedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    applySelectedFile(file);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    applySelectedFile(file);
  };

  const uploadHandler = async () => {
    if (!selectedFile || isSubmitting) {
      if (!selectedFile) {
        showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
      }
      return;
    }

    if (!animalId) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('description', description);

    try {
      setIsSubmitting(true);
      await uploadAnimalDocument(animalId, formData);
      showToast.success(toastMessages.DOCUMENT_UPLOADED);
      resetHandler();
      onUploaded();
    } catch (error: any) {
      const errorTitle = error?.response?.title;
      if (errorTitle) {
        showToast.error(errorTitle);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={resetHandler} ignoreBackdrop width={556}>
      <div className="add-document-modal">
        <h2>{ANIMAL_CONSTANTS.UPLOAD_DOCUMENT}</h2>

        <input
          id="uploadDocument"
          type="file"
          className="upload-document-input"
          accept={ACCEPTED_DOCUMENT_FILE_TYPES}
          onChange={fileChangedHandler}
          data-testid="Choose-Document"
        />

        <label
          htmlFor="uploadDocument"
          className="drop-area"
          onDragOver={event => event.preventDefault()}
          data-testid="document-drop-area"
          onDrop={onDrop}>
          <UploadImageSVG className="upload-icon" />
          <p className="drag-n-drop-text">{ANIMAL_CONSTANTS.DRAG_DROP_A_FILE}</p>
          <p className="supported-formats-text">
            {ANIMAL_CONSTANTS.DOCUMENT_SUPPORTED_FORMATS}{' '}
            <strong>{ANIMAL_CONSTANTS.DOCUMENT_SUPPORTED_FORMATS_LIST}</strong>
          </p>
          <p className="upload-limits-text">{ANIMAL_CONSTANTS.DOCUMENT_UPLOAD_LIMITS}</p>
          {selectedFile && <p className="selected-file-name">{selectedFile.name}</p>}
        </label>

        <div className="description-field">
          <label htmlFor="document-description">{ANIMAL_CONSTANTS.DESCRIPTION}</label>
          <textarea
            id="document-description"
            placeholder={ANIMAL_CONSTANTS.ENTER_DOCUMENT_DESCRIPTION}
            value={description}
            onChange={event => setDescription(event.target.value)}
            rows={4}
          />
        </div>

        <div className="modal-footer">
          <button type="button" className="red button medium" onClick={resetHandler} disabled={isSubmitting}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button
            type="button"
            className={selectedFile && !isSubmitting ? 'green button medium' : 'green button medium disabled'}
            onClick={uploadHandler}
            disabled={!selectedFile || isSubmitting}>
            {BUTTON_CONSTANTS.UPDATE}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddDocumentModal;
